import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, X, Loader2, ClipboardCheck, Package, Check } from "lucide-react";
import { toast } from "sonner";
import { tx } from "@/i18n";

export const SUPPLY_ACTIONS = [
  { key: "costa_key", label: "Costa Key" },
  { key: "owner", label: "Eigenaar beslist" },
  { key: "none", label: "Geen actie nodig" },
];
const CHOICE_LABEL = { costa_key: "Costa Key regelt het", self: "Ik regel het zelf" };
const actionLabel = (k) => tx(SUPPLY_ACTIONS.find((a) => a.key === k)?.label || k);
const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

function SupplyRow({ item, onChange, onRemove }) {
  return (
    <div className="rounded-lg border border-border p-2.5 space-y-2" data-testid={`supply-row-${item.id}`}>
      <div className="flex items-center gap-2">
        <Input value={item.text} onChange={(e) => onChange({ ...item, text: e.target.value })} placeholder={tx("Wat is er nodig?")} className="h-9 text-sm" data-testid={`supply-text-${item.id}`} />
        <button type="button" onClick={onRemove} className="text-muted-foreground hover:text-destructive p-1" data-testid={`supply-remove-${item.id}`}><X className="h-4 w-4" /></button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {SUPPLY_ACTIONS.map((a) => (
          <button key={a.key} type="button" onClick={() => onChange({ ...item, action: a.key })} data-testid={`supply-action-${a.key}-${item.id}`}
            className={`px-3 py-1 rounded-full text-xs border transition-colors duration-150 active:scale-95 ${item.action === a.key ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"}`}>
            {tx(a.label)}
          </button>
        ))}
      </div>
    </div>
  );
}

function SupplyStatus({ item, sent }) {
  if (item.action === "owner" && sent) {
    return item.owner_choice
      ? <Badge className="bg-emerald-600 text-white" data-testid={`supply-choice-${item.id}`}>{tx("Keuze eigenaar")}: {tx(CHOICE_LABEL[item.owner_choice])}</Badge>
      : <Badge variant="outline" className="border-amber-500 text-amber-600" data-testid={`supply-choice-${item.id}`}>{tx("Wacht op keuze eigenaar")}</Badge>;
  }
  return null;
}

export function SuppliesBlock({ supplies, setSupplies, editable, sent }) {
  const update = (i, v) => setSupplies(supplies.map((x, j) => (j === i ? v : x)));
  return (
    <div className="py-3 border-b border-border" data-testid="report-supplies">
      <p className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Package className="h-4 w-4 text-primary" /> {tx("Benodigdheden")}</p>
      {editable ? (
        <div className="space-y-2">
          {supplies.map((x, i) => <SupplyRow key={x.id} item={x} onChange={(v) => update(i, v)} onRemove={() => setSupplies(supplies.filter((_, j) => j !== i))} />)}
          <Button type="button" size="sm" variant="outline" onClick={() => setSupplies([...supplies, { id: newId(), text: "", action: "costa_key" }])} className="gap-1.5" data-testid="supply-add-btn">
            <Plus className="h-4 w-4" /> {tx("Benodigdheid toevoegen")}
          </Button>
        </div>
      ) : supplies.length === 0 ? <p className="text-sm text-muted-foreground">—</p> : (
        <ul className="space-y-1.5">
          {supplies.map((x) => (
            <li key={x.id} className="text-sm flex flex-wrap items-center gap-1.5" data-testid={`supply-item-${x.id}`}>
              <span className="font-medium">{x.text}</span>
              <Badge variant="outline">{actionLabel(x.action)}</Badge>
              <SupplyStatus item={x} sent={sent} />
              {x.task_id && <Badge variant="outline" className="border-primary/50 text-primary gap-1" data-testid={`supply-task-${x.id}`}><ClipboardCheck className="h-3 w-3" /> {tx("Taak aangemaakt")}</Badge>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function SupplyChoice({ m, canDecide, onDone, mine }) {
  const [busy, setBusy] = useState("");
  const s = m.supply;
  const decide = async (choice) => {
    setBusy(choice);
    try { await api.post(`/reports/${s.report_id}/supplies/${s.item_id}/decide`, { choice }); toast.success(tx("Keuze opgeslagen")); onDone?.(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  if (s.choice) return <p className={`mt-2 text-xs font-semibold ${mine ? "text-primary-foreground" : "text-emerald-700"}`} data-testid={`chat-supply-choice-${m.id}`}><Check className="h-3.5 w-3.5 inline mr-1" />{tx(CHOICE_LABEL[s.choice])}{s.choice_by ? ` · ${s.choice_by.split(" ")[0]}` : ""}</p>;
  if (!canDecide) return <p className={`mt-2 text-xs italic ${mine ? "text-primary-foreground/90" : "text-amber-600"}`} data-testid={`chat-supply-choice-${m.id}`}>{tx("Wacht op keuze eigenaar")}</p>;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`chat-supply-actions-${m.id}`}>
      {Object.entries(CHOICE_LABEL).map(([k, l]) => (
        <Button key={k} size="sm" variant={k === "costa_key" ? "default" : "outline"} disabled={!!busy} onClick={() => decide(k)} className="h-8 text-xs" data-testid={`chat-supply-${k}-${m.id}`}>
          {busy === k && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />} {tx(l)}
        </Button>
      ))}
    </div>
  );
}
